// CTV-466 follow-up: standalone "Edit commercial" panel rendered inside
// EditCampaignModal. Replaces the legacy single-file SwapCreativeTabPanel
// with a UI that mirrors the wizard's commercial step — same multi-slot
// scheduler, same "add another commercial" affordance, same library picker,
// same per-slot CreativeSlotCard so every commercial reads as a peer
// regardless of whether it's already running or a new replacement awaiting
// review. Save POSTs the diff to /submissions/:id/update-creative-set;
// new commercials land as pending_review and the publisher's per-creative
// approve flow (CTV-457 Phase C) takes over from there. On approval a
// creative_swap push job updates the running Publica line items.
//
// Waymark: EditCampaignModal owns the AI commercial list and mounts the
// provider on the active one (CTV-653 / CTV-775); this panel stages commits
// and reports its slot pointers up. CTV-611 added useWaymarkRework (now useWaymarkEditorSession), a minimal
// login-for-creative client (no provider lifting), so a rejected Waymark
// commercial can be reopened, reworked, and routed back through this
// panel's upload path. File upload and library pick remain the other two
// replacement paths.

import React from 'react';
import { Loader2, Plus } from 'lucide-react';
import type {
  CreativeSlotState,
  PublisherConfig,
  SelfServiceCreativeRow,
  SelfServiceSubmissionRow,
} from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { putFileWithProgress } from './shared/putFileWithProgress';
import { buildStationPalette } from '../../lib/dateAxisRendering';
import {
  rederiveSegmentSharesOnDateChange,
  reseatSharesAfterRemoval,
  resetToEven,
  seedSegmentSharesForNewCreative,
  singleSegmentShareForWindow,
  type CreativeWindow,
} from './shared/rotationSegments';
import { useReusableLibrary, priorApprovalBadge } from './shared/useReusableLibrary';
import { REJECTION_REASONS_BY_CODE } from './rejectionReasons';
import { useWaymarkEditorSession } from './waymark/useWaymarkEditorSession';
import type { FinishedCommercial } from './waymark/useAiCommercials';
import { buildReceipt } from './waymark/receipt';

const EMPTY_FINISHED: FinishedCommercial[] = [];
import { useAuth } from './auth/useAuth';
import { useWaymarkOptional } from './waymark/context';
import WaymarkPanel from './waymark/WaymarkPanel';
import WaymarkDisclosure from './waymark/WaymarkDisclosure';
import type { AiCommercialsState } from './waymark/useAiCommercials';
import { displayedName } from '../../data/publisherConfigs';
import { validateAdSpecs, formatAdSpecViolations } from './shared/validateAdSpecs';
import MultiCreativeScheduler from './scheduler/MultiCreativeScheduler';
import CreativeSlotCard from './scheduler/CreativeSlotCard';
import LibraryPickerModal, { type LibraryPick } from './steps/LibraryPickerModal';
import { RestorePreviousModal } from './RestorePreviousModal';
import { approvedReviewGate } from './campaignSharedHelpers';
import PrimaryButton from './shared/PrimaryButton';
import Banner from './shared/Banner';
import CoverageBanner from './shared/CoverageBanner';
import ConcurrencyBanner from './shared/ConcurrencyBanner';
import { validateCreativeCoverage } from './shared/creativeCoverage';
import { validateCreativeConcurrency } from './shared/creativeConcurrency';

interface EditCommercialTabPanelProps {
  submission: SelfServiceSubmissionRow;
  cfg: PublisherConfig;
  advertiserId: string | null;
  // CTV-775: the campaign's AI commercials (EditCampaignModal owns the hook
  // and the provider). Committed = a slot here carries the id, so the panel
  // reports every slot pointer up via onCommittedCommercialIdsChange.
  aiCommercials?: AiCommercialsState;
  onCommittedCommercialIdsChange?: (ids: string[]) => void;
  onSubmitted: () => void;
}

// Working-slot shape — mirrors CreativeSlotState plus a `existingCreativeId`
// that the save handler uses to split the local set into the
// updateCreativeSet diff (updateExisting vs. addCreatives).
interface EditSlot extends CreativeSlotState {
  // null = freshly added in this session; a string id = an existing
  // selfservice_creatives row we're editing in place.
  existingCreativeId: string | null;
  // For existing creatives, the row's status so the pill can distinguish
  // 'Running' (active) from 'Awaiting review' (pending_review). null on
  // freshly added slots.
  existingStatus: 'active' | 'pending_review' | null;
  // CTV-611: true when this slot was created through the recovery block's
  // fix actions (upload / library pick / Waymark rework). Drives the
  // rotation note; the slot otherwise behaves as a normal add.
  isFix?: boolean;
  // CTV-611 / CTV-775: the AI commercial this slot's file came from (a
  // Waymark commit, a rework of a voided AI creative, or a saved AI creative
  // via fromCreative). Sent as addCreatives[].waymarkCommercialId on save.
  waymarkCommercialId?: string | null;
}

// CTV-487: "today" as YYYY-MM-DD in local time. Matches the API's
// truncationDate calculation (priorDateOnly(toDateOnlyString(new Date())))
// closely enough for the Gantt preview — exact agreement isn't required
// because the API recomputes server-side at save.
function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function priorYmd(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const prev = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) - 1));
  const y = prev.getUTCFullYear();
  const mo = String(prev.getUTCMonth() + 1).padStart(2, '0');
  const d = String(prev.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

function maxYmd(a: string, b: string): string {
  return a > b ? a : b;
}

function makeSlotId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `edit-slot-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function fromCreative(c: SelfServiceCreativeRow): EditSlot {
  const segmentShares =
    Array.isArray(c.segment_shares) && c.segment_shares.length > 0
      ? c.segment_shares.map((s) => ({
          startDate: s.start_date,
          endDate: s.end_date,
          share: s.share,
        }))
      : singleSegmentShareForWindow(c.creative_start_date, c.creative_end_date);
  return {
    slotId: makeSlotId(),
    existingCreativeId: c.id,
    existingStatus: c.status === 'pending_review' ? 'pending_review' : 'active',
    storagePath: c.storage_path,
    filename: c.filename,
    mimeType: c.mime_type ?? 'video/mp4',
    sizeBytes: c.size_bytes ?? 0,
    clickThroughUrl: c.click_through_url ?? '',
    segmentShares,
    startDate: c.creative_start_date,
    endDate: c.creative_end_date,
    // CTV-775: a saved AI creative keeps its pointer so the tab knows the
    // commercial is committed (and shows its poster on the disclosure row).
    waymarkCommercialId: c.waymark_commercial_id ?? null,
  };
}

function toRotationWindows(slots: EditSlot[]): CreativeWindow[] {
  return slots.map((s) => ({
    id: s.slotId,
    startDate: s.startDate,
    endDate: s.endDate,
    segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
  }));
}

function applyDerivedShares(slots: EditSlot[], derived: CreativeWindow[]): EditSlot[] {
  const byId = new Map(derived.map((d) => [d.id, d]));
  return slots.map((s) => {
    const d = byId.get(s.slotId);
    if (!d) return s;
    return {
      ...s,
      startDate: d.startDate,
      endDate: d.endDate,
      segmentShares: d.segmentShares.map((seg) => ({ ...seg })),
    };
  });
}

const EditCommercialTabPanel: React.FC<EditCommercialTabPanelProps> = ({
  submission,
  cfg,
  advertiserId,
  aiCommercials,
  onCommittedCommercialIdsChange,
  onSubmitted,
}) => {
  const accent = cfg.accentColor || cfg.primaryColor;
  const campaignStartDate = submission.campaign?.startDate ?? '';
  const campaignEndDate = submission.campaign?.endDate ?? '';

  const initialSlots = React.useMemo<EditSlot[]>(
    () =>
      (submission.creatives ?? [])
        .filter((c) => c.status === 'active' || c.status === 'pending_review')
        .map(fromCreative),
    [submission.creatives],
  );

  const [slots, setSlots] = React.useState<EditSlot[]>(initialSlots);
  // CTV-466 follow-up: mirror the backend's ADR-0007 prior-approval predicate
  // in the UI so newly added commercials whose (filename, size_bytes)
  // match a previously-approved entry in the station/advertiser library
  // render as "Pre-approved" instead of "Awaiting review". Lookup is by
  // exact (filename, sizeBytes) — same key the server uses in
  // hasPriorApproval — so the pill matches what updateCreativeSet will
  // actually do on save.
  const { items: libraryItems } = useReusableLibrary(cfg.slug, advertiserId);
  const priorApprovalByKey = React.useMemo(() => {
    const out = new Map<string, 'approved' | 'rejected'>();
    for (const it of libraryItems) {
      const badge = priorApprovalBadge(it);
      if (!badge) continue;
      const key = `${it.filename}::${it.size_bytes ?? ''}`;
      // Library is sorted newest-first; only record the first verdict per key.
      if (!out.has(key)) out.set(key, badge);
    }
    return out;
  }, [libraryItems]);
  // Track which original creative ids the user has removed during this
  // session so the save handler can include them in removeCreativeIds.
  const [removedSourceIds, setRemovedSourceIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [files, setFiles] = React.useState<Record<string, File | null>>({});
  const [uploadingSlotId, setUploadingSlotId] = React.useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = React.useState<number | null>(null);
  const [uploadSucceededSlotId, setUploadSucceededSlotId] = React.useState<string | null>(null);
  const [pickerSlotId, setPickerSlotId] = React.useState<string | null>(null);
  // CTV-611: the recovery block's "Choose from library" opens the same
  // LibraryPickerModal, but the fix slot is only created once a pick lands
  // (deferred fix slot), so it needs its own open flag rather than a slotId.
  const [fixPickerOpen, setFixPickerOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  // CTV-661: confirmation dialog for "Restore previous commercial."
  const [confirmRestore, setConfirmRestore] = React.useState(false);

  // A slot only "exists" for the scheduler, palette, coverage, and badge once
  // it has a file picked or uploaded — mirrors UploadCreativeStep's
  // slotHasContent. Empty slots stay rendered as upload cards (with the
  // Remove affordance) but are otherwise hidden from rotation math so the
  // gantt doesn't surface a phantom bar / "awaiting review" pill before the
  // advertiser drops a file.
  const slotHasContent = React.useCallback(
    (s: EditSlot) => !!s.storagePath || !!s.filename,
    [],
  );
  const visibleSlots = React.useMemo(() => slots.filter(slotHasContent), [slots, slotHasContent]);

  // CTV-487: on a running campaign, only the rest-of-campaign window is
  // actionable. Past days already delivered (or didn't) — the buyer can't
  // retroactively cover them. Clamp the coverage / concurrency window to
  // max(campaignStart, today) so save-time validation only blocks on gaps
  // that affect future delivery. On not-yet-approved campaigns the
  // campaign hasn't started yet, so the original window is used as-is.
  const coverageWindowStart =
    submission.status === 'approved' && campaignStartDate && campaignStartDate < todayYmd()
      ? todayYmd()
      : campaignStartDate;

  // ADR-0006 per-day creative coverage on the post-diff state. Also remember
  // whether the gap pre-existed when the modal opened so the banner copy can
  // soften ("This campaign already had uncovered days…") for legacy state.
  // Coverage is computed from visibleSlots only so an empty extra slot
  // doesn't spuriously trip the banner.
  const initialCoverage = React.useMemo(
    () =>
      validateCreativeCoverage({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: initialSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, initialSlots],
  );
  const coverage = React.useMemo(
    () =>
      validateCreativeCoverage({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, visibleSlots],
  );
  const preExistingGap = !initialCoverage.covered;

  // ADR CTV-506 per-day concurrency cap on the post-diff state. Mirrors the
  // pre-existing-gap softening: if the violation already existed when the
  // modal opened, the banner softens its copy instead of accusing the buyer
  // of just-now creating it.
  const initialConcurrency = React.useMemo(
    () =>
      validateCreativeConcurrency({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: initialSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, initialSlots],
  );
  const concurrency = React.useMemo(
    () =>
      validateCreativeConcurrency({
        windowStart: coverageWindowStart,
        windowEnd: campaignEndDate,
        creatives: visibleSlots.map((s) => ({ startDate: s.startDate, endDate: s.endDate })),
      }),
    [coverageWindowStart, campaignEndDate, visibleSlots],
  );
  const preExistingOverCap = !initialConcurrency.withinCap;

  // CTV-487 / ADR-0012: client-side mirror of the API's second coverage
  // pass. Runs over the ACTIVE-ONLY projected Live Set — existing actives
  // (with their drag-applied dates), new prior-approved adds (auto-active),
  // and excludes pending_review adds. Removed creatives are treated as
  // truncated to "today − 1" so the preview Gantt matches what Publica
  // will actually deliver post-save.
  const isApproved = submission.status === 'approved';
  const pausedPreviewCoverage = React.useMemo(() => {
    if (!isApproved) return { covered: true, uncoveredRanges: [] as Array<{ startDate: string; endDate: string }> };
    const truncateDate = priorYmd(todayYmd());
    const ranges: Array<{ startDate: string; endDate: string }> = [];
    // Existing active rows still in the Live Set.
    for (const s of visibleSlots) {
      if (s.existingCreativeId == null) continue;
      if (s.existingStatus !== 'active') continue;
      ranges.push({ startDate: s.startDate, endDate: s.endDate });
    }
    // Existing active rows the buyer just removed in this session — treated
    // as truncated to "today − 1" on save.
    for (const sourceId of removedSourceIds) {
      const orig = (submission.creatives ?? []).find((c) => c.id === sourceId);
      if (!orig || orig.status !== 'active') continue;
      const originalEnd = orig.creative_end_date;
      const truncated = originalEnd < truncateDate ? originalEnd : truncateDate;
      if (truncated >= orig.creative_start_date) {
        ranges.push({ startDate: orig.creative_start_date, endDate: truncated });
      }
    }
    // New adds whose (filename, size) match an approved library entry → auto-active.
    for (const s of visibleSlots) {
      if (s.existingCreativeId != null) continue;
      const key = `${s.filename}::${s.sizeBytes ?? ''}`;
      if (priorApprovalByKey.get(key) === 'approved') {
        ranges.push({ startDate: s.startDate, endDate: s.endDate });
      }
    }
    return validateCreativeCoverage({
      windowStart: coverageWindowStart,
      windowEnd: campaignEndDate,
      creatives: ranges,
    });
  }, [
    isApproved,
    visibleSlots,
    removedSourceIds,
    submission.creatives,
    priorApprovalByKey,
    coverageWindowStart,
    campaignEndDate,
  ]);
  // CTV-659: a scheduled campaign (future start) has no delivery to pause,
  // so the transition banner is suppressed until the campaign has started.
  const notYetStarted = !!campaignStartDate && campaignStartDate > todayYmd();
  const willPauseDelivery =
    isApproved && !notYetStarted && coverage.covered && !pausedPreviewCoverage.covered;

  // Pending swaps still awaiting publisher review; drives the paused-banner
  // "waiting for approval" copy. (CTV-658: the CTV-487 restore machinery
  // that used to live here is gone — the library picker's Pre-approved path
  // covers bringing a previously-run commercial back.)
  const pendingCreatives = React.useMemo(
    () => (submission.creatives ?? []).filter((c) => c.status === 'pending_review'),
    [submission.creatives],
  );

  // CTV-611 / ADR-0023: unresolved rejection = voided AND resolved_at null.
  // Drives the recovery block at the top of the panel. resolved_at is
  // stamped server-side on every open voided row the moment any recovery
  // add lands, so a handled rejection never re-renders the block.
  const unresolvedRejections = React.useMemo(
    () => (submission.creatives ?? []).filter((c) => c.status === 'voided' && !c.resolved_at),
    [submission.creatives],
  );
  // The newest rejection owns the context card and seeds the deferred fix
  // slot's window when there are several open rejections.
  const fixSource = React.useMemo(() => {
    if (unresolvedRejections.length === 0) return null;
    return [...unresolvedRejections].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  }, [unresolvedRejections]);

  // CTV-611: is delivery paused right now? Same active-only-coverage-of-today
  // concept the detail page's "Delivery paused" banner derives, computed from
  // the stored rows (not the in-session slots) because it describes the
  // campaign's current state, not the post-save preview.
  const pausedNow = React.useMemo(() => {
    if (!isApproved) return false;
    const today = todayYmd();
    const activeRanges = (submission.creatives ?? [])
      .filter((c) => c.status === 'active')
      .map((c) => ({ startDate: c.creative_start_date, endDate: c.creative_end_date }));
    const cov = validateCreativeCoverage({
      windowStart: campaignStartDate,
      windowEnd: campaignEndDate,
      creatives: activeRanges,
    });
    return cov.uncoveredRanges.some((r) => r.startDate <= today && r.endDate >= today);
  }, [isApproved, submission.creatives, campaignStartDate, campaignEndDate]);

  // CTV-457: positional palette indexed against visibleSlots so newly added
  // empty cards don't bump everyone's colour. Empty slots fall back to a
  // muted slate token in render.
  const paletteSize = Math.max(visibleSlots.length, 6);
  const palette = React.useMemo(
    () => buildStationPalette(cfg.primaryColor, cfg.secondaryColor, paletteSize),
    [cfg.primaryColor, cfg.secondaryColor, paletteSize],
  );
  const visibleColors = React.useMemo(
    () => Array.from({ length: visibleSlots.length }, (_, i) => palette[i % palette.length]),
    [palette, visibleSlots.length],
  );
  const visiblePosition = React.useCallback(
    (slotId: string) => visibleSlots.findIndex((s) => s.slotId === slotId),
    [visibleSlots],
  );

  // Detect whether anything actually changed against the initial set — drives
  // the Save button's disabled state.
  const hasChanges = React.useMemo(() => {
    if (removedSourceIds.size > 0) return true;
    if (slots.some((s) => s.existingCreativeId == null)) return true;
    const byId = new Map(slots.map((s) => [s.existingCreativeId, s]));
    return initialSlots.some((init) => {
      const current = byId.get(init.existingCreativeId);
      if (!current) return true;
      return (
        JSON.stringify(current.segmentShares) !== JSON.stringify(init.segmentShares) ||
        current.startDate !== init.startDate ||
        current.endDate !== init.endDate ||
        (current.clickThroughUrl || '') !== (init.clickThroughUrl || '')
      );
    });
  }, [slots, removedSourceIds, initialSlots]);

  // CTV-466 follow-up: when the patch changes the date window, re-derive
  // sibling segment shares per the rotation-segment rules so each slot's
  // segmentShares keep partitioning its [startDate, endDate] window. Without
  // this, dragging a bar edge or editing the end-date input leaves the
  // segmentShares pinned to the prior window and the backend rejects save
  // with "segmentShares do not partition its window". Mirrors
  // useCreativeSlots.updateSlot.
  const patchSlot = (slotId: string, patch: Partial<EditSlot>) => {
    setSlots((prev) => {
      const current = prev.find((s) => s.slotId === slotId);
      const willChangeDates =
        (patch.startDate !== undefined && patch.startDate !== current?.startDate) ||
        (patch.endDate !== undefined && patch.endDate !== current?.endDate);
      const patched = prev.map((s) => (s.slotId === slotId ? { ...s, ...patch } : s));
      if (!willChangeDates) return patched;
      const target = patched.find((s) => s.slotId === slotId);
      if (!target) return patched;
      const derived = rederiveSegmentSharesOnDateChange(
        toRotationWindows(prev),
        slotId,
        target.startDate,
        target.endDate,
      );
      return applyDerivedShares(patched, derived);
    });
  };

  // CTV-653: slotId + window computation hoisted out of the updater (pattern
  // from addFixSlot below) so callers get the new slotId back, and a
  // Waymark commit can stamp its AI commercial on the staged slot.
  const addSlot = (opts?: { waymarkCommercialId?: string | null }): string => {
    const slotId = makeSlotId();
    // CTV-487 / ADR-0012: on a running campaign, a new commercial added
    // mid-flight starts today (or campaign start, whichever is later) so
    // the stored window reflects when delivery actually begins. The
    // server applies the same defaulting at save when no explicit
    // startDate is provided — mirroring it here keeps the Gantt truthful
    // pre-save.
    const defaultStart =
      submission.status === 'approved' && campaignStartDate
        ? maxYmd(todayYmd(), campaignStartDate)
        : campaignStartDate;
    setSlots((prev) => {
      const next: EditSlot = {
        slotId,
        existingCreativeId: null,
        existingStatus: null,
        storagePath: null,
        filename: '',
        mimeType: 'video/mp4',
        sizeBytes: 0,
        clickThroughUrl: '',
        segmentShares: singleSegmentShareForWindow(defaultStart, campaignEndDate),
        startDate: defaultStart,
        endDate: campaignEndDate,
        waymarkCommercialId: opts?.waymarkCommercialId ?? null,
      };
      const prospective = [...prev, next];
      const derived = seedSegmentSharesForNewCreative(toRotationWindows(prev), {
        id: slotId,
        startDate: defaultStart,
        endDate: campaignEndDate,
      });
      return applyDerivedShares(prospective, derived);
    });
    return slotId;
  };

  // CTV-611 deferred fix slot: no slot is pre-created for a rejection; one is
  // created the moment a file lands through any of the recovery actions
  // (upload / library pick / Waymark rework). The window is seeded from the
  // newest unresolved voided row: startDate = max(today, voided start),
  // endDate = voided end; if the voided window already ended, fall back to
  // today -> campaign end. Everything else (segment-share seeding,
  // validation) behaves exactly like a normal add.
  const addFixSlot = (opts?: { waymarkCommercialId?: string | null }): string => {
    const slotId = makeSlotId();
    const today = todayYmd();
    let startDate =
      submission.status === 'approved' && campaignStartDate
        ? maxYmd(today, campaignStartDate)
        : campaignStartDate;
    let endDate = campaignEndDate;
    if (fixSource) {
      if (fixSource.creative_end_date < today) {
        startDate = today;
        endDate = campaignEndDate;
      } else {
        startDate = maxYmd(today, fixSource.creative_start_date);
        endDate = fixSource.creative_end_date;
      }
    }
    setSlots((prev) => {
      const next: EditSlot = {
        slotId,
        existingCreativeId: null,
        existingStatus: null,
        storagePath: null,
        filename: '',
        mimeType: 'video/mp4',
        sizeBytes: 0,
        clickThroughUrl: '',
        segmentShares: singleSegmentShareForWindow(startDate, endDate),
        startDate,
        endDate,
        isFix: true,
        waymarkCommercialId: opts?.waymarkCommercialId ?? null,
      };
      const prospective = [...prev, next];
      const derived = seedSegmentSharesForNewCreative(toRotationWindows(prev), {
        id: slotId,
        startDate,
        endDate,
      });
      return applyDerivedShares(prospective, derived);
    });
    return slotId;
  };

  const handleFixFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    void upload(file, addFixSlot());
  };

  // CTV-653 / CTV-775 Waymark wiring, mirroring the wizard's UploadCreativeStep.
  // The context is null until EditCampaignModal's lazy provider mounts on an
  // active AI commercial (or stays null when every commercial is committed);
  // every consumer below no-ops on null. Committed = a slot carries the
  // commercial's id (saved AI creatives and staged commits alike); the panel
  // reports those pointers up so the modal's active-commercial rule can run.
  const waymarkCtx = useWaymarkOptional();
  const { selectedAdvertiser } = useAuth();
  const [waymarkRejection, setWaymarkRejection] = React.useState<string | null>(null);
  // Seat website preferred, submission snapshot as fallback; local state
  // because the swap tab has no form context.
  const [businessWebsite, setBusinessWebsite] = React.useState<string>(
    () =>
      selectedAdvertiser?.businessWebsite ??
      submission.business_info?.businessWebsite ??
      '',
  );
  const activeCommercialId = waymarkCtx?.commercialId ?? null;
  const committedSlots = React.useMemo(
    () =>
      slots
        .filter((s) => !!s.waymarkCommercialId && (!!s.storagePath || !!s.filename))
        .map((s) => ({ slotId: s.slotId, storagePath: s.storagePath, commercialId: s.waymarkCommercialId as string })),
    [slots],
  );
  const committedIdsKey = committedSlots.map((s) => s.commercialId).join('|');
  React.useEffect(() => {
    onCommittedCommercialIdsChange?.(committedIdsKey ? committedIdsKey.split('|') : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the joined ids so an unchanged set never re-fires
  }, [committedIdsKey]);
  const activeCommitted =
    activeCommercialId != null && committedSlots.some((s) => s.commercialId === activeCommercialId);
  const allSettled = aiCommercials?.allSettled ?? false;
  // CTV-785: the receipt (finished commercials joined to their slots). Add to
  // campaign is wizard-only for now; the tab renders the rows without it.
  const finished = aiCommercials?.finished ?? EMPTY_FINISHED;
  const receipt = React.useMemo(() => buildReceipt(finished, slots), [finished, slots]);

  const handleCommitWaymark = async (blob: Blob, videoId: string, commercialId: string | null) => {
    const specs = await validateAdSpecs(blob);
    if (!specs.ok) {
      analytics.track(EVENTS.WAYMARK_REJECTED_SPECS, {
        station_slug: cfg.slug,
        violations: specs.violations.map((v) => v.kind),
      });
      setWaymarkRejection(formatAdSpecViolations(specs.violations));
      return;
    }
    setWaymarkRejection(null);
    analytics.track(EVENTS.WAYMARK_COMMITTED, { station_slug: cfg.slug });
    const file = new File([blob], `waymark-${videoId}.mp4`, { type: blob.type || 'video/mp4' });
    // Staged as a plain NEW add slot carrying the AI commercial's id; the
    // save assembly forwards it as addCreatives[].waymarkCommercialId.
    const slotId = addSlot({ waymarkCommercialId: commercialId });
    void upload(file, slotId);
  };

  // Commit bridge, reproduced from UploadCreativeStep: an explicit "Run this
  // commercial" fetches the rendered blob and stages it. A saved AI creative
  // (existingCreativeId set) is never replaced from here; its rework path is
  // the side editor session below, which also replaces a staged slot in place.
  const [isCommittingWaymark, setIsCommittingWaymark] = React.useState(false);
  const waymarkRenderedUrl = waymarkCtx?.renderedUrl ?? null;
  const waymarkVideoId = waymarkCtx?.videoId ?? null;

  const handleRunWaymark = async () => {
    if (!waymarkRenderedUrl || !waymarkVideoId) return;
    setIsCommittingWaymark(true);
    try {
      const res = await fetch(waymarkRenderedUrl);
      if (!res.ok) {
        console.warn('[waymark] commit: fetch returned', res.status);
        return;
      }
      const blob = await res.blob();
      await handleCommitWaymark(blob, waymarkVideoId, activeCommercialId);
    } catch (e) {
      console.warn('[waymark] commit failed', e);
    } finally {
      setIsCommittingWaymark(false);
    }
  };

  // CTV-775 Make another commercial: a new row on the campaign; generation
  // starts on the modal's kickoff once the provider mounts on it.
  const handleMakeAnother = async () => {
    if (!aiCommercials) return;
    analytics.track(EVENTS.WAYMARK_MAKE_ANOTHER_CLICKED, {
      commercial_ordinal: aiCommercials.total + 1,
    });
    await aiCommercials.create();
  };

  // CTV-611 rework + CTV-785 review: one side editor session (see
  // useWaymarkEditorSession) with its own SDK instance and body-appended
  // container, independent of the modal-level WaymarkProvider. Where the new
  // file lands depends on what the commercial is here: a staged (unsaved)
  // slot that carries it is replaced in place (review); otherwise it is a
  // rework of a saved creative and routes into a fresh fix slot carrying the
  // voided row's waymark_commercial_id so the new creative keeps the pointer.
  const editorSession = useWaymarkEditorSession({
    onFileReady: (file, waymarkCommercialId) => {
      const staged = slots.find(
        (s) => s.waymarkCommercialId === waymarkCommercialId && s.existingCreativeId == null,
      );
      if (staged) {
        void upload(file, staged.slotId);
        return;
      }
      void upload(file, addFixSlot({ waymarkCommercialId }));
    },
  });

  // CTV-785 Review on a staged slot. The active (uncommitted) commercial
  // opens in the provider's editor; a committed one opens in the side editor
  // session by its own access token, so the active commercial's generation
  // is never interrupted. One editor at a time: ignored while a session is live.
  const openEditor = waymarkCtx?.openEditor;
  const handleCustomizeSlot = (commercialId: string) => {
    if (commercialId === activeCommercialId && openEditor) {
      void openEditor();
      return;
    }
    if (editorSession.session) return;
    const token = aiCommercials?.tokenOf(commercialId);
    if (!token) return;
    void editorSession.openForCommercial({ commercialId, commercialToken: token, slug: cfg.slug });
  };

  // CTV-611: once any new content-bearing slot exists in this session, the
  // recovery block collapses (visibleSlots is already filtered to slots with
  // content). Saving any add stamps resolved_at server-side, so the banner
  // only ever promises what save will do; removing the slot before saving
  // brings the banner back. Fully derived, no sticky state.
  const hasNewContentSlot = React.useMemo(
    () => visibleSlots.some((s) => s.existingCreativeId == null),
    [visibleSlots],
  );

  // CTV-661 (ADR-0012 revival): "Restore previous commercial." The server
  // derives which retired pre-swap creatives are restorable
  // (restorablePreSwapCreativeIds); the rows themselves are already on
  // submission.creatives (the loader includes `retired`). Offered only for the
  // two gated-approved states (approvedReviewGate !== null) and hidden once the
  // buyer has staged a fresh fix this session (hasNewContentSlot): the two
  // recovery paths shouldn't compete. The restore window is the rest of the
  // campaign; a degenerate window (campaign already over) suppresses the banner.
  const restorableRows = React.useMemo(() => {
    const ids = submission.restorablePreSwapCreativeIds ?? [];
    if (ids.length === 0) return [] as SelfServiceCreativeRow[];
    return (submission.creatives ?? []).filter((c) => ids.includes(c.id));
  }, [submission.restorablePreSwapCreativeIds, submission.creatives]);
  const restoreGate = approvedReviewGate(submission);
  const restoreStart = campaignStartDate ? maxYmd(todayYmd(), campaignStartDate) : '';
  const canRestore =
    submission.status === 'approved' &&
    restoreGate !== null && // states 2 (under_review) & 4 (needs_fix) only
    restorableRows.length > 0 &&
    !hasNewContentSlot && // hide once a fix is staged this session
    !!campaignStartDate &&
    !!campaignEndDate &&
    restoreStart <= campaignEndDate; // rest-of-campaign window must be non-empty
  // State-aware ids to remove in the SAME restore save (RestorePreviousModal
  // header explains why): state 2 removes the pending swap ids; state 4
  // removes nothing so the voided row isn't laundered into an approval.
  const restoreRemoveIds = React.useMemo(
    () =>
      restoreGate === 'under_review'
        ? (submission.creatives ?? [])
            .filter((c) => c.status === 'pending_review')
            .map((c) => c.id)
        : [],
    [restoreGate, submission.creatives],
  );

  // CTV-606 (ported in CTV-611): the campaign was already in Paused Delivery
  // when the panel opened, so the Pause banner should describe the state
  // ("currently paused") rather than the transition ("Once you save").
  // Reuses pausedNow (stored-row, active-only coverage of today) instead of
  // CTV-606's separate initial-coverage memo, and yields to the recovery
  // block, which carries its own paused-state line when it is showing.
  const alreadyPaused = pausedNow && (fixSource == null || hasNewContentSlot);

  // CTV-611 rotation note: when a fix-originated slot's window overlaps a
  // still-active slot's window, both will deliver in rotation. Rendered
  // outside the recovery block (which collapses once the fix slot exists).
  // Copy branches on the fix's prior-approval state: a pre-approved pick
  // never enters review and starts running at save (ADR-0007).
  const rotationNote = React.useMemo(() => {
    const fix = visibleSlots.find((s) => s.isFix);
    if (!fix) return null;
    const active = visibleSlots.find(
      (s) =>
        s.existingStatus === 'active' &&
        s.startDate <= fix.endDate &&
        fix.startDate <= s.endDate,
    );
    if (!active) return null;
    const key = `${fix.filename}::${fix.sizeBytes ?? ''}`;
    return {
      activeFilename: active.filename,
      fixFilename: fix.filename,
      preApproved: priorApprovalByKey.get(key) === 'approved',
    };
  }, [visibleSlots, priorApprovalByKey]);

  const removeSlot = (slotId: string) => {
    // CTV-653 / CTV-775: removing a staged Waymark slot drops its pointer with
    // it, so the commercial is uncommitted (and active) again.
    setSlots((prev) => {
      const idx = prev.findIndex((s) => s.slotId === slotId);
      if (idx < 0) return prev;
      const removed = prev[idx];
      if (removed.existingCreativeId) {
        setRemovedSourceIds((r) => {
          const next = new Set(r);
          next.add(removed.existingCreativeId!);
          return next;
        });
      }
      const remaining = prev.filter((s) => s.slotId !== slotId);
      if (remaining.length === 0) return [];
      const derived = reseatSharesAfterRemoval(toRotationWindows(remaining));
      return applyDerivedShares(remaining, derived);
    });
    setFiles((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    // CTV-487 follow-up: if the buyer X's a slot whose file is still
    // uploading, clear the upload tracker so Save / Add / Restore don't
    // stay disabled when the orphaned PUT eventually resolves (or hangs).
    setUploadingSlotId((prev) => {
      if (prev === slotId) {
        setUploadPercent(null);
        return null;
      }
      return prev;
    });
    setUploadSucceededSlotId((prev) => (prev === slotId ? null : prev));
  };

  // Triggered when CreativeSlotCard's CreativeUploader picks a file. If the
  // slot is currently pointing at an existing creative row, we treat the
  // upload as a replacement — mark the old id as removed and convert this
  // slot to a fresh add.
  const handleSlotFileChange = (slotId: string, file: File | null) => {
    if (!file) {
      setFiles((prev) => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
      setUploadSucceededSlotId((prev) => (prev === slotId ? null : prev));
      return;
    }
    // CTV-775: a manual pick replaces any AI commercial on the slot, so the
    // commercial becomes uncommitted (and active) again.
    setSlots((prev) =>
      prev.map((s) => (s.slotId === slotId ? { ...s, waymarkCommercialId: null } : s)),
    );
    void upload(file, slotId);
  };

  const upload = async (file: File, slotId: string) => {
    setError(null);
    setUploadingSlotId(slotId);
    setUploadPercent(0);
    setUploadSucceededSlotId(null);
    setFiles((prev) => ({ ...prev, [slotId]: file }));
    try {
      const { uploadUrl, storagePath } = await apiClient.post<{
        uploadUrl: string;
        storagePath: string;
      }>('/creatives/upload-url', {
        slug: cfg.slug,
        filename: file.name,
        mimeType: file.type || 'video/mp4',
        sizeBytes: file.size,
      });
      const putRes = await putFileWithProgress(uploadUrl, file, {
        contentType: file.type || 'video/mp4',
        onProgress: setUploadPercent,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      // CTV-508: generate the server-side poster in the background. Don't await
      // — ffmpeg can take tens of seconds and would stall the success UI.
      // Renderers fall back to the `#t=0.5` <video> seek until it lands.
      void apiClient
        .post('/creatives/poster', { slug: cfg.slug, storagePath })
        .catch(() => {
          /* poster is decorative — never fail the swap over it */
        });
      setSlots((prev) =>
        prev.map((s) => {
          if (s.slotId !== slotId) return s;
          if (s.existingCreativeId) {
            setRemovedSourceIds((r) => {
              const next = new Set(r);
              next.add(s.existingCreativeId!);
              return next;
            });
          }
          return {
            ...s,
            existingCreativeId: null,
            existingStatus: null,
            storagePath,
            filename: file.name,
            mimeType: file.type || 'video/mp4',
            sizeBytes: file.size,
          };
        }),
      );
      setUploadSucceededSlotId(slotId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploadingSlotId(null);
      setUploadPercent(null);
    }
  };

  const applyLibraryPick = (slotId: string, pick: LibraryPick) => {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.slotId !== slotId) return s;
        if (s.existingCreativeId) {
          setRemovedSourceIds((r) => {
            const next = new Set(r);
            next.add(s.existingCreativeId!);
            return next;
          });
        }
        return {
          ...s,
          existingCreativeId: null,
          existingStatus: null,
          storagePath: pick.storagePath,
          filename: pick.filename,
          mimeType: pick.mimeType,
          sizeBytes: pick.sizeBytes,
          clickThroughUrl: pick.clickThroughUrl,
          // CTV-775: a library pick replaces any AI commercial on the slot.
          waymarkCommercialId: null,
        };
      }),
    );
    setFiles((prev) => ({ ...prev, [slotId]: null }));
  };

  const onSave = async () => {
    if (!hasChanges) return;
    if (slots.length === 0) {
      setError('Keep at least one commercial.');
      return;
    }
    const missingUploads = slots.filter((s) => !s.storagePath);
    if (missingUploads.length > 0) {
      setError('Every commercial needs a video file.');
      return;
    }
    const addCreatives = slots
      .filter((s) => s.existingCreativeId == null)
      .map((s) => ({
        filename: s.filename,
        storagePath: s.storagePath as string,
        mimeType: s.mimeType,
        sizeBytes: s.sizeBytes,
        clickThroughUrl: s.clickThroughUrl?.trim() || undefined,
        segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
        startDate: s.startDate,
        endDate: s.endDate,
        // CTV-611 / CTV-775: Waymark commits and rework fix slots carry the
        // AI commercial so the creative can be reopened in Waymark later and
        // its impressions settle as Waymark.
        ...(s.waymarkCommercialId ? { waymarkCommercialId: s.waymarkCommercialId } : {}),
      }));
    const updateExisting = slots
      .filter((s) => s.existingCreativeId != null)
      .map((s) => ({
        creativeId: s.existingCreativeId as string,
        segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
        startDate: s.startDate,
        endDate: s.endDate,
        clickThroughUrl: s.clickThroughUrl?.trim() || undefined,
      }));

    setSaving(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${submission.id}/update-creative-set`, {
        removeCreativeIds: Array.from(removedSourceIds),
        addCreatives,
        updateExisting,
      });
      analytics.track(EVENTS.CREATIVE_SET_UPDATED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
        added: addCreatives.length,
        updated: updateExisting.length,
        removed: removedSourceIds.size,
      });
      onSubmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save commercial changes');
    } finally {
      setSaving(false);
    }
  };

  const allCommercialsUploaded = slots.every((s) => !!s.storagePath);
  // Mirrors UploadCreativeStep: the scheduler only renders once at least two
  // slots have content. An empty extra slot doesn't get a bar.
  const showScheduler = visibleSlots.length >= 2;
  // Gate "Add another commercial" so the user can't stack empty upload
  // cards — every existing slot must have content first.
  const canAddSlot = slots.every(slotHasContent);

  return (
    <div className="space-y-5">
      {/* CTV-611 recovery block: one rose banner for all unresolved
          rejections, above the slots. Reviewer notes per rejected file, a
          read-only context card with the three fix actions, and (when
          delivery is paused) the inline Restore line. Collapses as soon as
          the buyer lands any new commercial this session: saving that add
          resolves the rejection, so the alert's job is done. */}
      {fixSource && !hasNewContentSlot && (
        <Banner tone="warn" label="Commercial rejected">
          <div className="space-y-3">
            <ul className="space-y-3">
              {unresolvedRejections.map((c) => (
                <li key={c.id}>
                  <div className="font-medium text-rose-900 mb-0.5">{c.filename}</div>
                  {c.rejection_reason_codes && c.rejection_reason_codes.length > 0 && (
                    <ul className="space-y-0.5">
                      {c.rejection_reason_codes.map((code) => {
                        const r = REJECTION_REASONS_BY_CODE[code];
                        if (!r) return null;
                        return (
                          <li key={code} className="text-rose-800 leading-relaxed">
                            <span className="font-medium text-rose-900">{r.label}</span>
                            {r.advertiserText && (
                              <>
                                {': '}
                                <span className="text-rose-700/90">{r.advertiserText}</span>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {c.rejection_reason && (
                    <div className="mt-1 pl-3 border-l-2 border-rose-200 text-rose-800 leading-relaxed italic">
                      "{c.rejection_reason}"
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {/* Read-only rejected-file context card. NOT an EditSlot: the
                voided row is never edited in place and never enters the save
                payload; the actions below create a fresh fix slot instead. */}
            <div className="rounded-lg border border-rose-200 bg-white/70 px-3.5 py-3">
              <div className="text-[12px] font-semibold text-slate-900 tracking-tight truncate">
                {fixSource.filename}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Was scheduled {fixSource.creative_start_date} to {fixSource.creative_end_date}. Send a fixed commercial and {cfg.name} will review it again.
              </div>
              <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                <label
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full text-white cursor-pointer transition-all hover:brightness-110 active:scale-[0.98] ${
                    saving || uploadingSlotId !== null ? 'opacity-50 pointer-events-none' : ''
                  }`}
                  style={{
                    background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
                    boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${cfg.primaryColor}`,
                  }}
                >
                  Upload a fixed file
                  <input
                    type="file"
                    accept="video/*"
                    className="sr-only"
                    aria-label="Upload a fixed file"
                    onChange={handleFixFileInput}
                    disabled={saving || uploadingSlotId !== null}
                  />
                </label>
                {advertiserId && (
                  <button
                    type="button"
                    onClick={() => setFixPickerOpen(true)}
                    disabled={saving || uploadingSlotId !== null}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50 transition-all"
                  >
                    Choose from library
                  </button>
                )}
                {fixSource.waymark_commercial_id && !editorSession.unavailableCreativeIds.has(fixSource.id) && (
                  <button
                    type="button"
                    onClick={() => {
                      void editorSession.openForCreative({
                        creativeId: fixSource.id,
                        waymarkCommercialId: fixSource.waymark_commercial_id as string,
                      });
                    }}
                    disabled={editorSession.isBusy || saving || uploadingSlotId !== null}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50 transition-all"
                  >
                    {editorSession.isBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                    Rework in Waymark
                  </button>
                )}
              </div>
              {editorSession.unavailableCreativeIds.has(fixSource.id) && (
                <p className="text-[11px] text-rose-700 mt-2">
                  This commercial can't be reopened in Waymark. Upload a fixed file instead.
                </p>
              )}
              {editorSession.error && <p className="text-[11px] text-rose-700 mt-2">{editorSession.error}</p>}
            </div>

            {/* CTV-658: the inline Restore line is gone with the rest of the
                CTV-487 restore affordance — the library picker's Pre-approved
                path covers bringing an old commercial back. */}
          </div>
        </Banner>
      )}

      <p className="text-[13px] text-slate-600">
        Add, replace, remove, or reschedule commercials. New commercials are reviewed by {cfg.name} before they
        can run.
      </p>

      {/* CTV-611 rotation note: lives outside the recovery block so it
          survives the block's collapse once the fix slot exists. Neutral
          tone: it describes the future schedule, not the rejection. */}
      {rotationNote && (
        <p className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          {rotationNote.preApproved ? (
            <>
              <strong>{rotationNote.fixFilename}</strong> is pre-approved and will start running as
              soon as you save, based on the schedule you enter below.
            </>
          ) : (
            <>
              <strong>{rotationNote.activeFilename}</strong> keeps running while{' '}
              <strong>{rotationNote.fixFilename}</strong> is reviewed. Once approved, both will run
              based on the schedule you enter below.
            </>
          )}
        </p>
      )}

      <div className="space-y-5">
        {slots.map((slot) => {
          const fileForSlot = files[slot.slotId] ?? null;
          const pos = visiblePosition(slot.slotId);
          const hasContent = pos >= 0;
          // Empty slots render as upload-only cards: no scheduler position,
          // no colour-coded dot in the palette, no status pill. CreativeSlotCard
          // surfaces "New commercial" (vs. "Commercial N") when index === -1.
          const color = hasContent ? visibleColors[pos] : '#cbd5e1';
          // Status pill only renders for content-bearing slots. Four states:
          //   • Existing row, status='active' → "Running" (live now).
          //   • Existing row, status='pending_review' → "Awaiting review"
          //     (publisher hasn't decisioned it yet).
          //   • New add whose (filename, sizeBytes) matches an approved
          //     library entry → "Pre-approved" (ADR-0007 prior-approval
          //     mirrors what the server will record on save — no review
          //     queue stop).
          //   • New add without prior approval → "New — awaiting review".
          let statusPill: { label: string; tone: 'neutral' | 'pending' } | undefined;
          if (!hasContent) {
            statusPill = undefined;
          } else if (slot.existingCreativeId != null) {
            statusPill =
              slot.existingStatus === 'pending_review'
                ? { label: 'Awaiting review', tone: 'pending' as const }
                : { label: 'Running', tone: 'neutral' as const };
          } else {
            const key = `${slot.filename}::${slot.sizeBytes ?? ''}`;
            const prior = slot.filename ? priorApprovalByKey.get(key) : undefined;
            statusPill =
              prior === 'approved'
                ? { label: 'Pre-approved', tone: 'neutral' as const }
                : { label: 'New — awaiting review', tone: 'pending' as const };
          }
          return (
            <CreativeSlotCard
              key={slot.slotId}
              slot={slot}
              slug={cfg.slug}
              file={fileForSlot}
              accentColor={accent}
              color={color}
              index={hasContent ? pos : -1}
              removable
              uploading={uploadingSlotId === slot.slotId}
              uploadPercent={uploadingSlotId === slot.slotId ? uploadPercent : null}
              uploadError={null}
              uploadSucceeded={uploadSucceededSlotId === slot.slotId}
              statusPill={statusPill}
              onFileChange={(f) => handleSlotFileChange(slot.slotId, f)}
              onClickThroughUrlChange={(next) => patchSlot(slot.slotId, { clickThroughUrl: next })}
              onRemove={() => removeSlot(slot.slotId)}
              onPickFromLibrary={advertiserId ? () => setPickerSlotId(slot.slotId) : undefined}
              // CTV-775: a staged (unsaved) AI commercial can be re-customized
              // in place; saved AI creatives take the Rework in Waymark path.
              onCustomize={
                slot.waymarkCommercialId && slot.existingCreativeId == null && aiCommercials
                  ? () => handleCustomizeSlot(slot.waymarkCommercialId as string)
                  : undefined
              }
            />
          );
        })}
      </div>

      {canAddSlot && (
        <button
          type="button"
          // Not a direct handler reference: addSlot now takes an opts bag,
          // and the click event must not be passed as one.
          onClick={() => addSlot()}
          disabled={saving || uploadingSlotId !== null}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-dashed border-slate-300 text-[12px] font-semibold tracking-tight text-slate-600 hover:bg-slate-50 hover:border-slate-500 disabled:opacity-40 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add another commercial
        </button>
      )}

      {/* CTV-653: Waymark disclosure for the Swap session. Rendered even
          while the context is null (static row; expanding shows a muted
          Connecting line) so the affordance is stable while the lazy
          provider loads. The panel itself only mounts once expanded with a
          live context. */}
      <WaymarkDisclosure
        accentColor={accent}
        primaryColor={cfg.primaryColor}
        slug={cfg.slug}
        receipt={receipt}
        allSettled={allSettled}
        activeCommitted={activeCommitted}
        onCustomize={openEditor ? () => void openEditor() : undefined}
        onRun={handleRunWaymark}
        onMakeAnother={aiCommercials ? () => void handleMakeAnother() : undefined}
        isCommitting={isCommittingWaymark}
        isMakingAnother={aiCommercials?.isCreating ?? false}
      >
        <WaymarkPanel
          accentColor={accent}
          primaryColor={cfg.primaryColor}
          slug={cfg.slug}
          waymarkCommitted={activeCommitted}
          allSettled={allSettled}
          receipt={receipt}
          onCustomizeCommitted={handleCustomizeSlot}
          reviewSession={editorSession.session}
          reviewError={editorSession.error}
          activeOrdinal={activeCommercialId ? aiCommercials?.ordinalOf(activeCommercialId) : undefined}
          publisherName={displayedName(cfg)}
          rejection={waymarkRejection}
          onClearRejection={() => setWaymarkRejection(null)}
          businessWebsite={businessWebsite}
          onBusinessWebsiteChange={setBusinessWebsite}
        />
      </WaymarkDisclosure>

      {showScheduler && (
        <MultiCreativeScheduler
          slots={visibleSlots}
          colors={visibleColors}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          accentColor={accent}
          slug={cfg.slug}
          onSlotChange={(slotId, patch) => patchSlot(slotId, patch)}
          onResetWeights={() => {
            setSlots((prev) => applyDerivedShares(prev, resetToEven(toRotationWindows(prev))));
          }}
        />
      )}

      {visibleSlots.length > 0 && !coverage.covered && (
        <CoverageBanner
          uncoveredRanges={coverage.uncoveredRanges}
          surface="swap"
          prefix={
            preExistingGap
              ? 'This campaign has uncovered days. Fix them now to save your changes.'
              : undefined
          }
          slots={visibleSlots.map((s) => ({
            id: s.slotId,
            filename: s.filename,
            startDate: s.startDate,
            endDate: s.endDate,
          }))}
          onExtendLeftCreative={(id, newEnd) => patchSlot(id, { endDate: newEnd })}
          onExtendRightCreative={(id, newStart) => patchSlot(id, { startDate: newStart })}
        />
      )}

      {visibleSlots.length > 0 && !concurrency.withinCap && (
        <ConcurrencyBanner
          overCapRanges={concurrency.overCapRanges}
          prefix={
            preExistingOverCap
              ? 'This campaign already has too many commercials running on these dates. Fix them now to save your changes.'
              : undefined
          }
        />
      )}

      {/* CTV-487 / CTV-606 (ported in CTV-611): Paused Delivery banner. Two
          variants:
          - alreadyPaused: campaign is in Paused Delivery right now (state).
            Copy describes the current state, no "Once you save" wording.
          - willPauseDelivery: the buyer's pending diff will introduce a
            pause on save (transition). Keeps the original "Once you save"
            wording, which reads correctly for a fresh mid-modal edit. */}
      {(alreadyPaused || willPauseDelivery) && (
        <Banner tone="info" label={alreadyPaused ? 'Delivery paused' : 'Delivery pause'}>
          {(() => {
            if (alreadyPaused) {
              const pendingFilename = pendingCreatives[0]?.filename ?? null;
              return pendingFilename
                ? `Your campaign is currently paused, waiting for ${cfg.name} to approve ${pendingFilename}.`
                : `Your campaign is currently paused. Add a commercial below to resume delivery.`;
            }
            const firstGap = pausedPreviewCoverage.uncoveredRanges[0];
            const today = todayYmd();
            const startsToday = firstGap && firstGap.startDate <= today;
            return startsToday
              ? `Once you save, your campaign's delivery will pause until ${cfg.name} approves your new commercial. Most reviews finish within 1 business day, and delivery resumes automatically.`
              : `Once you save, your campaign's delivery will pause from ${firstGap?.startDate ?? ''} until ${cfg.name} approves the new commercial.`;
          })()}
        </Banner>
      )}

      {/* CTV-661 (ADR-0012 revival): one-click restore of the exact set that
          was delivering before this swap. Distinct from the library picker's
          Pre-approved path (which re-adds one file at a time): Restore brings
          back the WHOLE prior set atomically with a confirmation dialog. Only
          for the two gated-approved states, and hidden once a fresh fix is
          staged this session. */}
      {canRestore && (
        <Banner
          tone="caution"
          label="Restore previous commercial"
          rightSlot={
            <button
              type="button"
              onClick={() => setConfirmRestore(true)}
              disabled={saving || uploadingSlotId !== null}
              className="text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 disabled:opacity-50 transition-all"
            >
              Restore →
            </button>
          }
        >
          Bring back the commercial{restorableRows.length > 1 ? 's' : ''} that {restorableRows.length > 1 ? 'were' : 'was'} running
          before this change.
        </Banner>
      )}

      {error && <Banner tone="warn" label="Couldn't save">{error}</Banner>}

      <div className="flex flex-col items-end gap-1.5 pt-2 border-t border-slate-100">
        <PrimaryButton
          type="button"
          onClick={onSave}
          disabled={
            !hasChanges ||
            saving ||
            uploadingSlotId !== null ||
            !allCommercialsUploaded ||
            !coverage.covered ||
            !concurrency.withinCap
          }
          accentColor={accent}
          primaryColor={cfg.primaryColor}
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Save changes
        </PrimaryButton>
      </div>

      {(pickerSlotId || fixPickerOpen) && advertiserId && (
        <LibraryPickerModal
          open
          slug={cfg.slug}
          advertiserId={advertiserId}
          onClose={() => {
            setPickerSlotId(null);
            setFixPickerOpen(false);
          }}
          onPick={(pick) => {
            if (pickerSlotId) {
              applyLibraryPick(pickerSlotId, pick);
              setPickerSlotId(null);
            } else {
              // CTV-611 deferred fix slot: created only once the pick lands.
              applyLibraryPick(addFixSlot(), pick);
              setFixPickerOpen(false);
            }
          }}
        />
      )}

      {/* CTV-661: the Restore confirmation. Executes the atomic remove-pending
          + re-add-prior save immediately on Confirm, so any unsaved staged
          edits in this session are discarded by construction. onRestored wires
          to the same onSubmitted the Save button uses (→ onCreativeSwapped +
          close). Guarded by canRestore so it can't outlive its gate. */}
      {confirmRestore && canRestore && (
        <RestorePreviousModal
          submission={submission}
          cfg={cfg}
          creatives={restorableRows}
          removeCreativeIds={restoreRemoveIds}
          onCancel={() => setConfirmRestore(false)}
          onRestored={() => {
            setConfirmRestore(false);
            onSubmitted();
          }}
        />
      )}
    </div>
  );
};

export default EditCommercialTabPanel;
